/**
 * Caption recognition settings: the custom dictionary, find/replace rules and
 * the transcription toggles for one captioning flow.
 *
 * Core stores them as `flow.settings` and takes the complete block on
 * `PUT /captions/:id/recognition { settings }`:
 *
 *   vocab:              [string | { content, sounds_like: [string] }]   ≤ 500, JSON.stringify(vocab) ≤ 32,768 chars
 *   replacements:       [{ from, to }]                                   ≤ 100, `from` is a regex
 *   removeDisfluencies: boolean
 *   profanity:          'off' | 'mask' | 'remove'
 *   speakers:           boolean, with maxSpeakers 2–20
 *   outputLocale:       'en-GB' etc.; absent = the language's default spelling
 *   maxDelaySec:        0.7–4
 *   model:              not exposed; sent back only when the loaded block had it
 *
 * `null` and empty lists mean "not set". Core rejects any other field, so
 * nothing outside these nine is ever sent. The worker validates everything
 * again; the checks here exist so the editor can put each error on the row
 * and field it belongs to.
 */

import { isProfanitySupported } from './captionLanguages';

export const VOCAB_MAX = 500;
/** Characters of `JSON.stringify(vocab)`, not bytes. */
export const VOCAB_MAX_CHARS = 32_768;
export const TERM_MAX_WORDS = 6;
export const SOUNDS_LIKE_MAX = 3;
export const REPLACEMENTS_MAX = 100;
export const FIND_MIN = 2;
export const FIND_MAX = 200;
export const REPLACE_MAX = 200;
export const SPEAKERS_MIN = 2;
export const SPEAKERS_MAX = 20;
export const SPEAKERS_DEFAULT = 4;
export const DELAY_MIN = 0.7;
export const DELAY_MAX = 4;
export const DELAY_DEFAULT = 1.5;
/** The dictionary gets a search box past this many terms. */
export const SEARCH_THRESHOLD = 20;
/** The size meter shows once the dictionary uses this share of VOCAB_MAX_CHARS. */
export const SIZE_METER_RATIO = 0.5;

export const PROFANITY_OPTIONS = [
	{ value: 'off', label: 'Off' },
	{ value: 'mask', label: 'Mask (f******)' },
	{ value: 'remove', label: 'Remove' },
];

const LOCALES = [
	{ code: 'en-US', label: 'English (US)' },
	{ code: 'en-GB', label: 'English (UK)' },
	{ code: 'en-AU', label: 'English (Australia)' },
	{ code: 'es-ES', label: 'Spanish (Spain)' },
	{ code: 'es-MX', label: 'Spanish (Mexico)' },
	{ code: 'pt-BR', label: 'Portuguese (Brazil)' },
	{ code: 'pt-PT', label: 'Portuguese (Portugal)' },
	{ code: 'fr-FR', label: 'French (France)' },
	{ code: 'fr-CA', label: 'French (Canada)' },
];
const LOCALE_RE = /^[a-z]{2}-[A-Z]{2}$/;

const KNOWN_KEYS = ['vocab', 'replacements', 'removeDisfluencies', 'profanity', 'speakers', 'maxSpeakers', 'outputLocale', 'maxDelaySec'];

// Tabs and other control characters, plus invisible format characters (zero-width space, ...).
// eslint-disable-next-line no-control-regex
const CONTROL_RE = /[\u0000-\u001f\u007f-\u009f]|\p{Cf}/u;

let keySeq = 0;
const nextKey = (prefix) => `${prefix}${++keySeq}`;

export const newTermRow = (term = '', soundsLike = []) => ({ key: nextKey('t'), term, soundsLike: [...soundsLike] });
export const newReplacementRow = (from = '', to = '') => ({ key: nextKey('r'), from, to });

/**
 * Spelling options for a flow: the locales of the spoken language (spelling
 * applies to speech recognition; translated tracks are not affected), plus a
 * saved locale outside that list so it can still be seen and cleared.
 */
export function localeOptions(source = '', current = '') {
	const list = source ? LOCALES.filter((l) => localeMatchesSource(l.code, source)) : [];
	if (current && !list.some((l) => l.code === current)) {
		list.push(LOCALES.find((l) => l.code === current) || { code: current, label: current });
	}
	return list;
}

/** Whether a spelling locale (en-GB) belongs to the spoken language (en). */
export function localeMatchesSource(locale, source) {
	if (!locale || !source) return true;
	return String(locale).split('-')[0].toLowerCase() === String(source).toLowerCase();
}

export function localeLabel(code) {
	return LOCALES.find((l) => l.code === code)?.label || code;
}

const clampNumber = (v, min, max, fallback) => {
	const n = Number(v);
	if (!Number.isFinite(n) || v === null || v === '') return fallback;
	return Math.min(max, Math.max(min, n));
};

/** Editor draft from core's `flow.settings` (absent or `{}` on an older core). */
export function draftFromSettings(raw) {
	const s = raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {};
	const vocab = Array.isArray(s.vocab) ? s.vocab : [];
	const replacements = Array.isArray(s.replacements) ? s.replacements : [];
	const extra = typeof s.model === 'string' && s.model ? { model: s.model } : {};
	return {
		vocab: vocab.map((entry) => {
			if (typeof entry === 'string') return newTermRow(entry);
			if (entry && typeof entry === 'object') {
				const sounds = Array.isArray(entry.sounds_like) ? entry.sounds_like.filter((x) => typeof x === 'string') : [];
				return newTermRow(typeof entry.content === 'string' ? entry.content : '', sounds);
			}
			return newTermRow('');
		}),
		replacements: replacements
			.filter((r) => r && typeof r === 'object')
			.map((r) => newReplacementRow(typeof r.from === 'string' ? r.from : '', typeof r.to === 'string' ? r.to : '')),
		removeDisfluencies: s.removeDisfluencies === true,
		profanity: ['off', 'mask', 'remove'].includes(s.profanity) ? s.profanity : 'off',
		speakers: s.speakers === true,
		maxSpeakers: Math.round(clampNumber(s.maxSpeakers, SPEAKERS_MIN, SPEAKERS_MAX, SPEAKERS_DEFAULT)),
		outputLocale: typeof s.outputLocale === 'string' && LOCALE_RE.test(s.outputLocale) ? s.outputLocale : '',
		maxDelaySec: clampNumber(s.maxDelaySec, DELAY_MIN, DELAY_MAX, DELAY_DEFAULT),
		extra,
	};
}

const isBlankTerm = (row) => !row.term.trim() && row.soundsLike.every((s) => !s.trim());
const isBlankReplacement = (row) => row.from === '' && row.to === '';

export const vocabEntry = (term, soundsLike) => {
	const content = term.trim();
	const sounds = soundsLike.map((s) => s.trim()).filter(Boolean);
	return sounds.length ? { content, sounds_like: sounds } : content;
};

/**
 * The complete settings block for `PUT /recognition`, plus the draft row key
 * behind each array index so a server error path (`vocab[12].content`) can
 * be put back on its row. Blank rows (nothing typed) are left out.
 * `source` is the flow's spoken language: profanity is sent as 'off' for a
 * language the filter doesn't support (per `catalog.profanitySources`).
 */
export function settingsFromDraft(draft, { source = '', catalog } = {}) {
	const vocabRows = draft.vocab.filter((r) => !isBlankTerm(r));
	const replacementRows = draft.replacements.filter((r) => !isBlankReplacement(r));
	const settings = {
		...draft.extra,
		vocab: vocabRows.map((r) => vocabEntry(r.term, r.soundsLike)),
		replacements: replacementRows.map((r) => ({ from: r.from, to: r.to })),
		removeDisfluencies: !!draft.removeDisfluencies,
		profanity: isProfanitySupported(source, catalog) ? (draft.profanity || 'off') : 'off',
		speakers: !!draft.speakers,
		maxDelaySec: Number(draft.maxDelaySec),
	};
	if (draft.speakers) settings.maxSpeakers = Number(draft.maxSpeakers);
	if (draft.outputLocale) settings.outputLocale = draft.outputLocale;
	return {
		settings,
		vocabKeys: vocabRows.map((r) => r.key),
		replacementKeys: replacementRows.map((r) => r.key),
	};
}

/** Order-independent-of-keys snapshot for dirty checks. */
export function draftSnapshot(draft, opts = {}) {
	return JSON.stringify(settingsFromDraft(draft, opts).settings);
}

export function serializedLength(value) {
	return (typeof value === 'string' ? value : JSON.stringify(value)).length;
}

/** Case- and accent-insensitive identity of a term, as the server dedupes. */
export function termKey(term) {
	return String(term || '')
		.trim()
		.normalize('NFD')
		.replace(/\p{M}/gu, '')
		.toLowerCase();
}

export const wordCount = (term) => term.trim().split(/\s+/).filter(Boolean).length;

/** First rule a term breaks, or ''. */
export function termError(term) {
	const t = String(term || '').trim();
	if (!t) return 'Enter the term.';
	if (CONTROL_RE.test(t)) return 'Remove tabs, control and invisible characters.';
	if (/[;/]/.test(t)) return '";" and "/" aren\'t allowed in a term.';
	if (wordCount(t) > TERM_MAX_WORDS) return `Keep it to ${TERM_MAX_WORDS} words or fewer.`;
	return '';
}

export function soundsLikeError(value) {
	const t = String(value || '').trim();
	if (!t) return '';
	if (CONTROL_RE.test(t)) return 'Remove tabs, control and invisible characters.';
	return '';
}

/** Browser's compile error for a Find pattern, or ''. */
export function regexError(pattern) {
	try {
		// eslint-disable-next-line no-new
		new RegExp(pattern);
		return '';
	} catch (err) {
		return err?.message || 'Not a valid regular expression.';
	}
}

/**
 * Field-level problems in the draft.
 *   rows:     { [rowKey]: { term?, soundsLike?, from?, to? } }   blocking
 *   dupes:    { [rowKey]: { of: rowKey, index, term } }          warning only: the server keeps the first copy
 *   sections: { vocab?, replacements?, maxSpeakers?, maxDelaySec?, outputLocale? }
 *   count:    number of blocking errors
 */
export function validateDraft(draft) {
	const rows = {};
	const dupes = {};
	const sections = {};
	let count = 0;
	const flag = (key, field, message) => {
		if (!message) return;
		rows[key] = rows[key] || {};
		if (!rows[key][field]) count += 1;
		rows[key][field] = message;
	};

	const seen = new Map();
	draft.vocab.forEach((row, index) => {
		if (isBlankTerm(row)) return;
		flag(row.key, 'term', termError(row.term));
		const sounds = row.soundsLike.filter((s) => s.trim());
		if (sounds.length > SOUNDS_LIKE_MAX) flag(row.key, 'soundsLike', `Up to ${SOUNDS_LIKE_MAX} "sounds like" per term.`);
		else flag(row.key, 'soundsLike', sounds.map(soundsLikeError).find(Boolean) || '');
		const k = termKey(row.term);
		if (!k) return;
		if (seen.has(k)) {
			const first = seen.get(k);
			dupes[row.key] = { of: first.key, index: first.index, term: first.term };
		} else {
			seen.set(k, { key: row.key, index, term: row.term.trim() });
		}
	});

	const { settings } = settingsFromDraft(draft);
	if (settings.vocab.length > VOCAB_MAX) {
		sections.vocab = `The dictionary holds up to ${VOCAB_MAX} terms. Remove ${settings.vocab.length - VOCAB_MAX}.`;
		count += 1;
	} else if (serializedLength(settings.vocab) > VOCAB_MAX_CHARS) {
		sections.vocab = 'The dictionary is over the 32,768-character limit. Remove some terms or "sounds like" entries.';
		count += 1;
	}

	draft.replacements.forEach((row) => {
		if (isBlankReplacement(row)) return;
		if (row.from.length < FIND_MIN || row.from.length > FIND_MAX) {
			flag(row.key, 'from', `Find needs ${FIND_MIN} to ${FIND_MAX} characters.`);
		} else {
			flag(row.key, 'from', regexError(row.from));
		}
		if (row.to.length > REPLACE_MAX) flag(row.key, 'to', `Replace can be up to ${REPLACE_MAX} characters.`);
	});
	if (settings.replacements.length > REPLACEMENTS_MAX) {
		sections.replacements = `Up to ${REPLACEMENTS_MAX} replacements. Remove ${settings.replacements.length - REPLACEMENTS_MAX}.`;
		count += 1;
	}

	if (draft.speakers) {
		const n = Number(draft.maxSpeakers);
		if (!Number.isInteger(n) || n < SPEAKERS_MIN || n > SPEAKERS_MAX) {
			sections.maxSpeakers = `Enter a whole number from ${SPEAKERS_MIN} to ${SPEAKERS_MAX}.`;
			count += 1;
		}
	}
	const d = Number(draft.maxDelaySec);
	if (!Number.isFinite(d) || d < DELAY_MIN || d > DELAY_MAX) {
		sections.maxDelaySec = `Enter a value from ${DELAY_MIN} to ${DELAY_MAX} seconds.`;
		count += 1;
	}
	if (draft.outputLocale && !LOCALE_RE.test(draft.outputLocale)) {
		sections.outputLocale = 'Pick a spelling from the list.';
		count += 1;
	}

	return { rows, dupes, sections, count, vocabCount: settings.vocab.length, vocabChars: serializedLength(settings.vocab) };
}

/**
 * Maps a server error `path` onto the draft:
 *   'vocab[12].content' -> { kind: 'row', key, field: 'term' }
 *   'vocab[3].sounds_like[1]' -> { kind: 'row', key, field: 'soundsLike' }
 *   'replacements[2].from' -> { kind: 'row', key, field: 'from' }
 *   'maxSpeakers' / 'vocab' -> { kind: 'section', field }
 */
export function locateServerError(path, vocabKeys, replacementKeys) {
	const p = String(path || '');
	const m = p.match(/^(vocab|replacements)\[(\d+)\](?:\.([a-z_]+))?/i);
	if (m) {
		const [, list, idx, sub] = m;
		const keys = list === 'vocab' ? vocabKeys : replacementKeys;
		const key = keys[Number(idx)];
		if (key) {
			let field;
			if (list === 'vocab') field = sub === 'sounds_like' ? 'soundsLike' : 'term';
			else field = sub === 'to' ? 'to' : 'from';
			return { kind: 'row', key, field };
		}
		return { kind: 'section', field: list };
	}
	const top = p.split(/[.[]/)[0];
	if (top === 'model') return { kind: 'general' };
	if (KNOWN_KEYS.includes(top)) return { kind: 'section', field: top === 'speakers' ? 'maxSpeakers' : top };
	return { kind: 'general' };
}

// ------------------------------------------------------------ Import / export

function parseCsvLine(line) {
	const out = [];
	let cur = '';
	let quoted = false;
	for (let i = 0; i < line.length; i += 1) {
		const ch = line[i];
		if (quoted) {
			if (ch === '"' && line[i + 1] === '"') {
				cur += '"';
				i += 1;
			} else if (ch === '"') {
				quoted = false;
			} else {
				cur += ch;
			}
		} else if (ch === '"' && cur === '') {
			quoted = true;
		} else if (ch === ',') {
			out.push(cur);
			cur = '';
		} else {
			cur += ch;
		}
	}
	out.push(cur);
	return out;
}

/**
 * Parses an uploaded list. One entry per line: `Term` or `Term|pron1,pron2`.
 * A `.csv` file may instead have two columns `term,sounds_like` with the
 * pronunciations separated by `;` (a `term,sounds_like` header is skipped).
 * Blank lines and `#` comments are ignored. Bad lines are reported, never fatal.
 *
 * -> { entries: [{ line, term, soundsLike }], errors: [{ line, text, reason }] }
 */
export function parseDictionaryText(text, filename = '') {
	const csv = /\.csv$/i.test(filename);
	const entries = [];
	const errors = [];
	const lines = String(text || '').replace(/^\uFEFF/, '').split(/\r\n|\n|\r/);
	lines.forEach((raw, i) => {
		const line = i + 1;
		const trimmed = raw.trim();
		if (!trimmed || trimmed.startsWith('#')) return;
		let term;
		let sounds;
		if (csv && !trimmed.includes('|')) {
			const cols = parseCsvLine(trimmed);
			if (i === 0 && /^term$/i.test(cols[0].trim())) return;
			if (cols.length > 2) {
				errors.push({ line, text: raw, reason: 'Too many columns. Use term,sounds_like with ";" between pronunciations.' });
				return;
			}
			term = cols[0];
			sounds = (cols[1] || '').split(';');
		} else {
			const bar = trimmed.indexOf('|');
			term = bar >= 0 ? trimmed.slice(0, bar) : trimmed;
			sounds = bar >= 0 ? trimmed.slice(bar + 1).split(',') : [];
		}
		term = term.trim();
		sounds = sounds.map((s) => s.trim()).filter(Boolean);
		const reason = termError(term)
			|| (sounds.length > SOUNDS_LIKE_MAX ? `More than ${SOUNDS_LIKE_MAX} "sounds like" entries.` : '')
			|| sounds.map(soundsLikeError).find(Boolean)
			|| '';
		if (reason) errors.push({ line, text: raw, reason });
		else entries.push({ line, term, soundsLike: sounds });
	});
	return { entries, errors };
}

/**
 * What importing `parsed` into `existingRows` would do, in either mode.
 * `merge` keeps the current terms and adds new ones; `replace` swaps the
 * whole dictionary. Duplicates (of the dictionary in merge mode, or earlier
 * in the file) are skipped. Past VOCAB_MAX the import stops: `overCap` says
 * how many valid new terms did not fit.
 */
export function planImport(parsed, existingRows, mode) {
	const seen = new Set(mode === 'merge' ? existingRows.filter((r) => r.term.trim()).map((r) => termKey(r.term)) : []);
	const base = mode === 'merge' ? existingRows.filter((r) => !isBlankTerm(r)).length : 0;
	const room = Math.max(0, VOCAB_MAX - base);
	const toAdd = [];
	let duplicates = 0;
	let overCap = 0;
	for (const e of parsed.entries) {
		const k = termKey(e.term);
		if (seen.has(k)) {
			duplicates += 1;
			continue;
		}
		seen.add(k);
		if (toAdd.length >= room) {
			overCap += 1;
			continue;
		}
		toAdd.push(e);
	}
	return { toAdd, duplicates, overCap, errors: parsed.errors.length, room };
}

/** Applies a planned import to the dictionary rows. */
export function applyImport(existingRows, plan, mode) {
	const added = plan.toAdd.map((e) => newTermRow(e.term, e.soundsLike));
	if (mode === 'replace') return added;
	return [...existingRows.filter((r) => !isBlankTerm(r)), ...added];
}

const csvCell = (v) => (/[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);

/** `Term` or `Term|pron1,pron2`, one per line; the import format. */
export function exportText(rows) {
	return rows
		.filter((r) => !isBlankTerm(r))
		.map((r) => {
			const sounds = r.soundsLike.map((s) => s.trim()).filter(Boolean);
			return sounds.length ? `${r.term.trim()}|${sounds.join(',')}` : r.term.trim();
		})
		.join('\n') + '\n';
}

/** Two-column CSV with a header; pronunciations joined with `;`. */
export function exportCsv(rows) {
	const body = rows
		.filter((r) => !isBlankTerm(r))
		.map((r) => `${csvCell(r.term.trim())},${csvCell(r.soundsLike.map((s) => s.trim()).filter(Boolean).join(';'))}`);
	return ['term,sounds_like', ...body].join('\n') + '\n';
}

/** "42 terms · 3 replacements" for a list summary. */
export function summaryLabel(summary) {
	const v = Number(summary?.vocabCount) || 0;
	const r = Number(summary?.replacementsCount) || 0;
	const parts = [`${v} ${v === 1 ? 'term' : 'terms'}`];
	if (r) parts.push(`${r} ${r === 1 ? 'replacement' : 'replacements'}`);
	return parts.join(' · ');
}

export function normalizeSettingsSummary(raw) {
	const s = raw && typeof raw === 'object' ? raw : {};
	const vocabCount = Number.isFinite(Number(s.vocabCount)) ? Number(s.vocabCount) : 0;
	const replacementsCount = Number.isFinite(Number(s.replacementsCount)) ? Number(s.replacementsCount) : 0;
	return {
		vocabCount,
		replacementsCount,
		hasSettings: typeof s.hasSettings === 'boolean' ? s.hasSettings : vocabCount + replacementsCount > 0,
	};
}
